<script setup lang="ts">
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import type { Tag, TagTextFlags } from '@/api'
import { useTagEditor } from '@/composables/useTagEditor'
import { chipStyle } from '@/todos/tags'
import UiGroup from '@/components/UiGroup.vue'
import UiList from '@/components/UiList.vue'
import UiRow from '@/components/UiRow.vue'

// How a tag is ordered and grouped on a todo. Each option has its explanation under it (no tooltips), in this order.
const props = defineProps<{ tagId: number }>()
const { t } = useI18n()
const editor = useTagEditor()
const tag = computed(() => editor.tags.value.find((existing) => existing.id === props.tagId))

type Flag =
  | 'is_category'
  | 'ignore_for_grouping'
  | 'is_additional'
  | 'keep_last'
  | keyof TagTextFlags
interface Option {
  flag: Flag
  label: string
  hint: string
  disabledByCategory?: boolean // (meaningless for a tag that is a category)
}
const options: Option[] = [
  { flag: 'is_category', label: 'settings.tags.isCategory', hint: 'settings.tags.isCategoryHint' },
  {
    flag: 'ignore_for_grouping',
    label: 'settings.tags.ignoreForGrouping',
    hint: 'settings.tags.ignoreForGroupingHint',
  },
  {
    flag: 'is_additional',
    label: 'settings.tags.isAdditional',
    hint: 'settings.tags.isAdditionalHint',
    disabledByCategory: true,
  },
  {
    flag: 'keep_last',
    label: 'settings.tags.keepLast',
    hint: 'settings.tags.keepLastHint',
    disabledByCategory: true,
  },
]

// Where the tag is also written into the todo's text, for apps that can't show tags (any combination, none = off).
const textOptions: Option[] = [
  {
    flag: 'text_title_start',
    label: 'settings.tags.textTitleStart',
    hint: 'settings.tags.textTitleStartHint',
  },
  {
    flag: 'text_title_end',
    label: 'settings.tags.textTitleEnd',
    hint: 'settings.tags.textTitleEndHint',
  },
  {
    flag: 'text_body_start',
    label: 'settings.tags.textBodyStart',
    hint: 'settings.tags.textBodyStartHint',
  },
  {
    flag: 'text_body_end',
    label: 'settings.tags.textBodyEnd',
    hint: 'settings.tags.textBodyEndHint',
  },
]

function disabled(current: Tag, option: Option): boolean {
  return !!option.disabledByCategory && current.is_category
}
</script>

<template>
  <div v-if="tag" class="tag-arrangement" :data-tag-id="tag.id">
    <UiGroup>
      <p class="crumb">
        <span class="label" :style="chipStyle(tag.color)"># {{ tag.name }}</span>
      </p>
    </UiGroup>
    <UiGroup>
      <UiList>
        <UiRow as="label" v-for="option in options" :key="option.flag" class="option">
          <span class="text">
            <span class="title">{{ t(option.label) }}</span>
            <span class="ui-hint">{{ t(option.hint) }}</span>
          </span>
          <input
            type="checkbox"
            :checked="tag[option.flag as Exclude<Flag, keyof TagTextFlags>]"
            :disabled="disabled(tag, option)"
            @change="editor.setFlag(tag, option.flag, ($event.target as HTMLInputElement).checked)"
          />
        </UiRow>
      </UiList>
    </UiGroup>
    <UiGroup>
      <h3 class="section-title">{{ t('settings.tags.textSection') }}</h3>
      <p class="ui-hint section-hint">{{ t('settings.tags.textSectionHint') }}</p>
      <UiList data-section="text">
        <UiRow as="label" v-for="option in textOptions" :key="option.flag" class="option">
          <span class="text">
            <span class="title">{{ t(option.label) }}</span>
            <span class="ui-hint">{{ t(option.hint) }}</span>
          </span>
          <input
            type="checkbox"
            :checked="tag[option.flag as keyof TagTextFlags]"
            :data-flag="option.flag"
            @change="editor.setFlag(tag, option.flag, ($event.target as HTMLInputElement).checked)"
          />
        </UiRow>
      </UiList>
    </UiGroup>
  </div>
  <p v-else-if="editor.loaded.value" class="ui-hint">{{ t('settings.tags.gone') }}</p>
  <p v-else>{{ t('common.loading') }}</p>
</template>

<style scoped>
.crumb {
  margin: 0;
}

.label {
  padding: 0.1rem 0.5rem;
  border-radius: 999px;
}

.section-title {
  margin: 0 0 0.25rem;
  font-size: 1rem;
}

.section-hint {
  margin: 0 0 0.5rem;
}

.option {
  align-items: flex-start;
}

.text {
  display: flex;
  flex: 1;
  flex-direction: column;
  gap: 0.15rem;
  min-width: 0;
}

.option input {
  width: auto;
  margin: 0.2rem 0 0;
}
</style>
